// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------
// Three experiences, one app. Which one you get is decided by your role, not
// by the URL you type:
//
//   customer            -> the portal
//   agent / senior      -> the operations console
//   manager / admin     -> the console plus administration
//
// Every page is lazy-loaded, and every route is wrapped in a permission gate.
// A customer navigating directly to /ops/queue gets a refusal, not a screen
// that half-renders and then fails its data fetch.
// ---------------------------------------------------------------------------

import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from './auth/AuthContext';
import { EmptyState, Button, PanelSkeleton } from './ui/Primitives';
import AppShell from './layouts/AppShell';
import SignIn from './pages/SignIn';

// --- Customer portal ---
const PortalOverview = lazy(() => import('./pages/portal/Overview'));
const MyRequests = lazy(() => import('./pages/portal/MyRequests'));
const NewRequest = lazy(() => import('./pages/portal/NewRequest'));
const RequestDetail = lazy(() => import('./pages/portal/RequestDetail'));
const PortalIncidents = lazy(() => import('./pages/portal/Incidents'));
const SupportPlan = lazy(() => import('./pages/portal/SupportPlan'));

// --- Operations console ---
const Queue = lazy(() => import('./pages/ops/Queue'));
const Workspace = lazy(() => import('./pages/ops/Workspace'));
const OpsIncidents = lazy(() => import('./pages/ops/Incidents'));
const IncidentDetail = lazy(() => import('./pages/ops/IncidentDetail'));
const Customers = lazy(() => import('./pages/ops/Customers'));
const CustomerDetail = lazy(() => import('./pages/ops/CustomerDetail'));
const SlaMonitor = lazy(() => import('./pages/ops/SlaMonitor'));
const Analytics = lazy(() => import('./pages/ops/Analytics'));
const AuditLog = lazy(() => import('./pages/ops/AuditLog'));
const Feedback = lazy(() => import('./pages/ops/Feedback'));

// --- Clinic log access (Monitoring & Diagnostics) ---
const ClinicAccess = lazy(() => import('./pages/platform/ClinicAccess'));
const ClinicLogs = lazy(() => import('./pages/platform/ClinicLogs'));

// --- Shared ---
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const Article = lazy(() => import('./pages/Article'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Search = lazy(() => import('./pages/Search'));
const Profile = lazy(() => import('./pages/Profile'));

// --- Administration ---
const AdminPlans = lazy(() => import('./pages/admin/Plans'));
const AdminSla = lazy(() => import('./pages/admin/SlaPolicies'));
const AdminPriority = lazy(() => import('./pages/admin/PriorityRules'));
const AdminCategories = lazy(() => import('./pages/admin/Categories'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminNotifications = lazy(() => import('./pages/admin/NotificationRules'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));

function PageFallback() {
  return (
    <div className="page">
      <div className="skel" style={{ height: 24, width: 220, marginBottom: 'var(--s5)' }} />
      <div className="panel" style={{ padding: 'var(--s4)' }}>
        <PanelSkeleton lines={7} height={14} />
      </div>
    </div>
  );
}

/** Refuses a route the signed-in role does not hold the permission for. */
function Gate({ permission, anyOf, children }) {
  const { can, canAny } = useAuth();
  const allowed = permission ? can(permission) : anyOf ? canAny(anyOf) : true;

  if (!allowed) {
    return (
      <div className="page">
        <EmptyState icon={ShieldAlert} title="You do not have access to this page">
          Your role does not include this area of the support platform. If you believe that is
          wrong, ask an administrator to review your permissions.
        </EmptyState>
      </div>
    );
  }
  return children;
}

export default function App() {
  const { status, isInternal, error, loadSession } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <div className="col" style={{ gap: 'var(--s3)', alignItems: 'center' }}>
          <div className="skel" style={{ height: 30, width: 190 }} />
          <span className="small subtle">Loading your session…</span>
        </div>
      </div>
    );
  }

  if (status === 'anonymous') {
    // A non-401 failure is an outage, not a missing session — say so rather
    // than presenting a sign-in form that cannot possibly work.
    if (error && error.status !== 401) {
      return (
        <div className="page" style={{ maxWidth: 560, marginTop: 'var(--s9)' }}>
          <EmptyState icon={ShieldAlert} title="The support service is unreachable">
            <div>{error.message}</div>
            <div style={{ marginTop: 'var(--s3)' }}>
              <Button variant="primary" onClick={loadSession}>Retry</Button>
            </div>
          </EmptyState>
        </div>
      );
    }
    return <SignIn />;
  }

  // Root goes to the right home screen for the role.
  const home = isInternal ? '/ops/queue' : '/portal';

  return (
    <AppShell>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="/signin" element={<Navigate to={home} replace />} />

          {/* --- Customer portal --- */}
          <Route path="/portal" element={<PortalOverview />} />
          <Route path="/portal/requests" element={<MyRequests />} />
          <Route path="/portal/requests/new" element={<Gate permission="ticket.create"><NewRequest /></Gate>} />
          <Route path="/portal/requests/:id" element={<RequestDetail />} />
          <Route path="/portal/incidents" element={<PortalIncidents />} />
          <Route path="/portal/plan" element={<SupportPlan />} />

          {/* --- Operations console --- */}
          <Route path="/ops/queue" element={<Gate permission="ticket.view.all"><Queue /></Gate>} />
          <Route path="/ops/my-tickets" element={<Gate permission="ticket.view.all"><Queue lockedView="mine" /></Gate>} />
          <Route path="/ops/tickets/:id" element={<Gate permission="ticket.view.all"><Workspace /></Gate>} />
          <Route path="/ops/incidents" element={<Gate permission="incident.view.internal"><OpsIncidents /></Gate>} />
          <Route path="/ops/incidents/:id" element={<Gate permission="incident.view.internal"><IncidentDetail /></Gate>} />
          <Route path="/ops/customers" element={<Gate permission="customer.context.view"><Customers /></Gate>} />
          <Route path="/ops/customers/:id" element={<Gate permission="customer.context.view"><CustomerDetail /></Gate>} />
          <Route path="/ops/sla" element={<Gate permission="sla.monitor"><SlaMonitor /></Gate>} />
          <Route path="/ops/analytics" element={<Gate permission="analytics.view"><Analytics /></Gate>} />
          <Route path="/ops/audit" element={<Gate anyOf={['audit.view', 'customer.manage']}><AuditLog /></Gate>} />
          <Route path="/ops/feedback" element={<Gate permission="feedback.view"><Feedback /></Gate>} />

          {/* --- Clinic log access ---
              Staff ask a clinic for temporary access to its logs; the clinic
              approves from inside its own CliniqVoice account. The grant is
              held and re-checked by the CliniqVoice API, not here. */}
          <Route path="/platform/access-requests" element={<Gate permission="platform.access"><ClinicAccess /></Gate>} />
          <Route path="/platform/logs" element={<Gate permission="platform.access"><ClinicLogs /></Gate>} />

          {/* --- Shared --- */}
          <Route path="/kb" element={<Gate permission="kb.read"><KnowledgeBase /></Gate>} />
          <Route path="/kb/:id" element={<Gate permission="kb.read"><Article /></Gate>} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/search" element={<Search />} />
          <Route path="/profile" element={<Profile />} />

          {/* --- Administration --- */}
          <Route path="/admin/plans" element={<Gate permission="plan.manage"><AdminPlans /></Gate>} />
          <Route path="/admin/sla" element={<Gate permission="sla.manage"><AdminSla /></Gate>} />
          <Route path="/admin/priority" element={<Gate permission="priority_rules.manage"><AdminPriority /></Gate>} />
          <Route path="/admin/categories" element={<Gate permission="category.manage"><AdminCategories /></Gate>} />
          <Route path="/admin/teams" element={<Navigate to="/admin/users?tab=teams" replace />} />
          <Route
            path="/admin/users"
            element={
              <Gate anyOf={['user.manage', 'customer.manage', 'team.manage']}>
                <AdminUsers />
              </Gate>
            }
          />
          <Route path="/admin/notifications" element={<Gate permission="notification_rules.manage"><AdminNotifications /></Gate>} />
          <Route path="/admin/settings" element={<Gate permission="settings.manage"><AdminSettings /></Gate>} />

          <Route
            path="*"
            element={
              <div className="page">
                <EmptyState title="Page not found">
                  <div className="mono small">{location.pathname}</div>
                  <div style={{ marginTop: 'var(--s3)' }}>
                    <Button onClick={() => window.location.assign(home)}>
                      Back to {isInternal ? 'the queue' : 'support'}
                    </Button>
                  </div>
                </EmptyState>
              </div>
            }
          />
        </Routes>
      </Suspense>
    </AppShell>
  );
}
